import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataRoomSheet } from 'router/subrouters/DataRoom/models/DataRoom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  checkAreAllRequiredFieldsValid,
  checkHasEveryRequiredColumn,
  checkIfColumnIsValid,
  getArrayOfColleagueEmails,
} from './components/utils';
import { Loader } from 'ncoded-component-library';
import api from 'api';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { updateKeyInObject } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import BulkAploadActions from 'components/BulkAploadActions';
import UpdateDataRoomDrawer from '../UpdateDataRoomDrawer';
import {
  BODY_CONTENT_GENERATE,
  HEADER_CONTENT_GENERATE,
  KeyCell,
  KeyHeader,
} from './components';

import './BulkAddDataRooms.styles.scss';

type BulkAddDataRoomsProps = {
  className?: string;
  enterpriseId: string;
  draftDataRooms: DataRoomSheet[];
  columns: string[];
  setDraftDataRooms: React.Dispatch<React.SetStateAction<DataRoomSheet[]>>;
  setColumns: React.Dispatch<React.SetStateAction<string[]>>;
};

const BulkAddDataRooms: React.FC<BulkAddDataRoomsProps> = (props) => {
  const {
    className,
    enterpriseId,
    columns,
    draftDataRooms,
    setColumns,
    setDraftDataRooms,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [selectedDataRoom, setSelectedDataRoom] = useState<DataRoomSheet>();

  const [manageDRDrawer, manageDRDrawerRef] = useCallbackRef<SideDrawerRef>();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('bulk-add-data-rooms', 'white-panel', className);

  const count = useMemo(() => draftDataRooms?.length, [draftDataRooms?.length]);

  const numberOfProcessableColumns = useMemo(
    () => columns.filter(checkIfColumnIsValid).length,
    [columns],
  );

  const numberOfUnProcessableColumns = useMemo(
    () => columns.length - numberOfProcessableColumns,
    [columns, numberOfProcessableColumns],
  );

  const canSubmitContacts = useMemo(
    () =>
      !!count &&
      checkHasEveryRequiredColumn(columns) &&
      checkAreAllRequiredFieldsValid(draftDataRooms),
    [columns, count, draftDataRooms],
  );

  const handleEditDataRoom = useCallback(
    (contact: DataRoomSheet) => {
      setSelectedDataRoom(contact);
      manageDRDrawer.setOpen(true);
    },
    [manageDRDrawer],
  );

  const clearDataRooms = useCallback(() => {
    setSelectedDataRoom(null);
    setColumns([]);
    setDraftDataRooms([]);
  }, [setColumns, setDraftDataRooms]);

  const updateDataRoom = useCallback(
    ({ _id, ...rest }) =>
      setDraftDataRooms((old) =>
        old.map((el) => (el._id === _id ? { ...el, ...rest } : el)),
      ),
    [setDraftDataRooms],
  );

  const onUpdateColumn = useCallback(
    (key: string, newKey: string) => {
      setColumns((old) => old.map((el) => (el === key ? newKey : el)));
      setDraftDataRooms((old) =>
        old.map((el) => updateKeyInObject(el, key, newKey) as DataRoomSheet),
      );
    },
    [setColumns, setDraftDataRooms],
  );

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column]: (dataRoom: DataRoomSheet) => {
            if (checkIfColumnIsValid(column))
              return BODY_CONTENT_GENERATE[column]({
                dataRoom,
                column,
              });

            return <KeyCell column={column} dataRoom={dataRoom} />;
          },
        }),
        {},
      ),
    [columns],
  );

  const renderedHeader = useMemo(
    () =>
      columns.reduce(
        (acc, column, index) => ({
          ...acc,
          [column]: () => {
            if (checkIfColumnIsValid(column))
              return HEADER_CONTENT_GENERATE[column]({
                column,
                index,
                allColumns: columns,
                onUpdateColumn,
              });

            return (
              <KeyHeader
                column={column}
                index={index}
                allColumns={columns}
                onUpdateColumn={onUpdateColumn}
              />
            );
          },
        }),
        {},
      ),
    [columns, onUpdateColumn],
  );

  const handleBulkUpload = useCallback(async () => {
    const selectedDr = draftDataRooms.map(({ _id, colleagues, ...rest }) => {
      const obj = {
        ...rest,
        colleagues: colleagues ? getArrayOfColleagueEmails(colleagues) : [],
      };

      return Object.fromEntries(
        Object.entries(obj).filter(([key]) => checkIfColumnIsValid(key)),
      );
    });

    try {
      setIsLoading(true);
      await api.dataRoom.access.bulkCreateDataRoom(enterpriseId, selectedDr);
      popSuccess(
        t('drImportedSuccesfully', {
          count,
        }),
      );
      clearDataRooms();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    count,
    draftDataRooms,
    enterpriseId,
    t,
    popSuccess,
    popServerError,
    clearDataRooms,
  ]);

  return (
    <div className={classes}>
      {isLoading && <Loader className="fixed" />}

      <h2>{t('bulkUploadDataRooms', { count })}</h2>

      <div className="bulk-add-data-rooms__scroll">
        <ReorderResizeTable
          columns={columns}
          isDragDisabled
          isResizeDisabled
          data={draftDataRooms}
          tableContentTransformations={renderedContent}
          tableHeaderContentTransformations={renderedHeader}
          onRowClick={handleEditDataRoom}
        />
      </div>

      <BulkAploadActions
        title="uploadDataRooms"
        total={count}
        isDisabled={!canSubmitContacts}
        numberOfProcessableColumns={numberOfProcessableColumns}
        numberOfUnProcessableColumns={numberOfUnProcessableColumns}
        onClear={clearDataRooms}
        handleBulkUpload={handleBulkUpload}
      />

      <UpdateDataRoomDrawer
        ref={manageDRDrawerRef}
        dataRoom={selectedDataRoom}
        updateDataRoom={updateDataRoom}
      />
    </div>
  );
};

export default BulkAddDataRooms;
